import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type TripType, type TripTypeInput } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column } from '@/components/ui';
import { IconTrips } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { Drawer, Field } from '@/components/admin/Drawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

/** Trip Types — the service-tier catalogue (Premier / Logistics / Retail /
 *  Reserve) the customer app reads to resolve each home-grid slot to a backend
 *  trip_type code. content-svc OWNS the `trip_types` table (migration 049); the
 *  customer app reads it via content-svc's public GET /v1/content/home.
 *
 *  READ is genuinely LIVE here — the table IS in this Postgres, so the list +
 *  detail bind to real rows through admin-svc's shared read pool (every role with
 *  cms.read sees them). WRITES (create / edit / delete / activate) are authored
 *  by ops + superadmin (cms.manage — the same content-authoring permission as the
 *  CMS + zones); admin-svc PROXIES them to content-svc, which owns the table.
 *  Until content-svc deploys its internal trip-type admin endpoints (and the
 *  CONTENT_INTERNAL_SECRET is set) the writes return a clean, surfaced 503 — the
 *  drawer shows that reason verbatim rather than failing silently. Mirrors the
 *  legacy Filament TripTypeResource (id / name / vehicle_mode / sort /
 *  status / active_icon / inactive_icon). */
export function TripTypes() {
  // null = closed; 'new' = create; a TripType = edit that row.
  const [editing, setEditing] = useState<TripType | 'new' | null>(null);
  const [q, setQ] = useState('');
  // '' = any status; '1' = active only; '0' = inactive only.
  const [status, setStatus] = useState<'' | '1' | '0'>('');
  const canManage = useAdminPermission('cms.manage');

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'trip-types', q, status],
    queryFn: ({ signal }) =>
      adminApi.tripTypes({ q, status: status === '' ? undefined : Number(status) }, signal),
    placeholderData: (prev) => prev,
  });

  const unavailable = data && data.available === false;
  const rows = data?.items ?? [];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Trip types"
        subtitle="The service-tier catalogue the apps read for the home grid (owned by content-svc)."
        right={
          <SearchBox
            value={q}
            onChange={setQ}
            placeholder="Search trip types…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Trip types not yet available"
            description={
              data?.note ??
              'The trip_types table is owned by content-svc and is not in this Postgres yet (pending migration apply).'
            }
            icon={<IconTrips className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between gap-3">
            <StatusFilter value={status} onChange={setStatus} fetching={isFetching} />
            {canManage ? (
              <button type="button" onClick={() => setEditing('new')} className="btn-primary">
                New trip type
              </button>
            ) : null}
          </div>

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading trip types…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load trip types" description={(error as Error)?.message} />
            ) : (
              <TripTypesTable
                rows={rows}
                canManage={canManage}
                onEdit={(t) => setEditing(t)}
              />
            )}
          </Card>
        </>
      )}

      {editing ? (
        <TripTypeDrawer
          tripType={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </AdminPage>
  );
}

// ─── status filter (segmented control) ───────────────────────

/** A small segmented Any / Active / Inactive filter over the catalogue. Mirrors
 *  the legacy Filament status column's two states, plus an "any" default. */
function StatusFilter({
  value,
  onChange,
  fetching,
}: {
  value: '' | '1' | '0';
  onChange: (v: '' | '1' | '0') => void;
  fetching?: boolean;
}) {
  const opts: Array<{ key: '' | '1' | '0'; label: string }> = [
    { key: '', label: 'All' },
    { key: '1', label: 'Active' },
    { key: '0', label: 'Inactive' },
  ];
  return (
    <div className="flex items-center gap-2">
      <div className="inline-flex rounded-lg border border-ink-700 bg-ink-900 p-0.5">
        {opts.map((o) => (
          <button
            key={o.key || 'all'}
            type="button"
            onClick={() => onChange(o.key)}
            className={
              'rounded-md px-3 py-1 text-xs font-medium transition-colors ' +
              (value === o.key
                ? 'bg-brand-500 text-white'
                : 'text-text-secondary hover:bg-hover hover:text-slate-100')
            }
          >
            {o.label}
          </button>
        ))}
      </div>
      {fetching ? <Spinner /> : null}
    </div>
  );
}

// ─── table ───────────────────────────────────────────────────

/** The trip-types DataTable. Includes an inline activate/deactivate toggle + an
 *  Edit button for cms.manage roles, mirroring the Zones table. */
function TripTypesTable({
  rows,
  canManage,
  onEdit,
}: {
  rows: TripType[];
  canManage: boolean;
  onEdit: (t: TripType) => void;
}) {
  const qc = useQueryClient();
  // The id currently being toggled (per-row pending state).
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const toggle = useMutation({
    mutationFn: ({ tt, status }: { tt: TripType; status: 0 | 1 }) =>
      adminApi.setTripTypeStatus(tt.id, status),
    onMutate: ({ tt }) => setTogglingId(tt.id),
    onSettled: () => {
      setTogglingId(null);
      qc.invalidateQueries({ queryKey: ['admin', 'trip-types'] });
    },
  });

  const columns: Array<Column<TripType>> = [
    {
      key: 'id',
      header: 'Code',
      cell: (r) => <span className="metric text-xs text-text-muted">#{r.id}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      cell: (r) => (
        <div className="min-w-0">
          <div className="font-medium text-slate-100">{r.name || '—'}</div>
        </div>
      ),
    },
    {
      key: 'vehicle_mode',
      header: 'Vehicle mode',
      cell: (r) => <span className="text-slate-300">{r.vehicle_mode || '—'}</span>,
    },
    {
      key: 'sort',
      header: 'Sort',
      cell: (r) => <span className="metric text-xs text-text-secondary">{r.sort}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <Badge tone={r.status === 1 ? 'ok' : 'neutral'} dot>
          {r.status === 1 ? 'active' : 'inactive'}
        </Badge>
      ),
    },
  ];
  if (canManage) {
    columns.push({
      key: 'actions',
      header: '',
      align: 'right',
      cell: (r) => {
        const next: 0 | 1 = r.status === 1 ? 0 : 1;
        const busy = toggle.isPending && togglingId === r.id;
        return (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => toggle.mutate({ tt: r, status: next })}
              className="rounded-md border border-ink-700 px-2 py-1 text-xs text-slate-300 hover:bg-ink-800 disabled:opacity-50"
            >
              {busy ? '…' : r.status === 1 ? 'Deactivate' : 'Activate'}
            </button>
            <button
              type="button"
              onClick={() => onEdit(r)}
              className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
            >
              Edit
            </button>
          </div>
        );
      },
    });
  }

  return (
    <DataTable<TripType>
      columns={columns}
      rows={rows}
      rowKey={(r) => String(r.id)}
      stickyHeader
      empty={
        <EmptyState
          title="No trip types"
          description="No trip types match this filter."
          icon={<IconTrips className="h-6 w-6" />}
        />
      }
    />
  );
}

// ─── authoring drawer ────────────────────────────────────────

/** The editable form state. Numeric fields bind to strings (parsed on submit). */
interface FormState {
  id: string;
  name: string;
  vehicle_mode: string;
  sort: string;
  status: number; // 1 = active, 0 = inactive
  active_icon: string;
  inactive_icon: string;
}

/** TripTypeDrawer — the create/edit form. tripType=null ⇒ create; otherwise edit
 *  that row (seeded from it). On success it invalidates the list and closes.
 *
 *  RBAC: only ops + superadmin hold cms.manage; this drawer only ever opens for
 *  them (the page gates the New/Edit affordances on the same hook). admin-svc
 *  enforces the matrix server-side and 403s regardless. The writes proxy
 *  content-svc; until it deploys its internal trip-type admin endpoints the save
 *  returns a clean 503, which the drawer surfaces verbatim. */
function TripTypeDrawer({ tripType, onClose }: { tripType: TripType | null; onClose: () => void }) {
  const isEdit = tripType !== null;
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(() => seedForm(tripType));
  const [clientError, setClientError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: TripTypeInput) =>
      isEdit ? adminApi.updateTripType(tripType!.id, input) : adminApi.createTripType(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'trip-types'] });
      onClose();
    },
  });

  const del = useMutation({
    mutationFn: () => adminApi.deleteTripType(tripType!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'trip-types'] });
      onClose();
    },
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = () => {
    setClientError(null);
    const built = buildInput(form, isEdit);
    if ('error' in built) {
      setClientError(built.error);
      return;
    }
    mutation.mutate(built.input);
  };

  const busy = mutation.isPending || del.isPending;

  return (
    <Drawer
      title={isEdit ? `Edit trip type #${tripType!.id}` : 'New trip type'}
      subtitle={isEdit ? tripType!.name : 'Define a service tier'}
      onClose={onClose}
    >
      <div className="space-y-4">
        {isEdit ? (
          <div>
            <Field label="Code" value={`#${tripType!.id}`} />
            <Field label="Status" value={tripType!.status === 1 ? 'Active' : 'Inactive'} />
          </div>
        ) : null}

        <Card>
          <div className="space-y-3">
            {isEdit ? null : (
              <TextField
                label="Code (trip_type id)"
                hint="The backend trip_type code the app POSTs — e.g. 1 Premier · 4 Logistics · 13 Retail · 14 Reserve. This is the primary key, so it must be unique."
                value={form.id}
                onChange={(v) => set('id', v)}
                type="number"
                placeholder="1"
              />
            )}
            <TextField
              label="Name"
              hint="The tier name staff + the apps see (e.g. Premier)."
              value={form.name}
              onChange={(v) => set('name', v)}
              placeholder="Premier"
            />
            <TextField
              label="Vehicle mode"
              hint="The fleet/vehicle-mode id (18 = the Tesla fleet today)."
              value={form.vehicle_mode}
              onChange={(v) => set('vehicle_mode', v)}
              type="number"
              placeholder="18"
            />
            <TextField
              label="Sort"
              hint="The positional order the home grid indexes by (ascending)."
              value={form.sort}
              onChange={(v) => set('sort', v)}
              type="number"
              placeholder="0"
            />

            <div>
              <label className="block text-[11px] text-text-muted">Status</label>
              <select
                value={form.status}
                onChange={(e) => set('status', Number(e.target.value))}
                className="mt-1 input"
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
              <p className="mt-0.5 text-[11px] text-text-muted">
                Inactive tiers are hidden from the apps' home grid.
              </p>
            </div>

            <TextField
              label="Active icon path"
              hint="Optional icon asset path shown when the tile is selected."
              value={form.active_icon}
              onChange={(v) => set('active_icon', v)}
              placeholder=""
            />
            <TextField
              label="Inactive icon path"
              hint="Optional icon asset path shown when the tile is idle."
              value={form.inactive_icon}
              onChange={(v) => set('inactive_icon', v)}
              placeholder=""
            />
          </div>
        </Card>

        <div className="flex items-center gap-3">
          <button type="button" disabled={busy} onClick={onSubmit} className="btn-primary">
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create trip type'}
          </button>
          <button type="button" disabled={busy} onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          {isEdit ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (window.confirm('Delete this trip type? This cannot be undone.')) del.mutate();
              }}
              className="ml-auto rounded-md border border-danger/40 px-3 py-1.5 text-xs text-danger hover:bg-danger/10 disabled:opacity-50"
            >
              {del.isPending ? 'Deleting…' : 'Delete'}
            </button>
          ) : null}
        </div>
        {clientError ? <p className="text-xs text-danger">{clientError}</p> : null}
        {mutation.isError ? <p className="text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
        {del.isError ? <p className="text-xs text-danger">{(del.error as Error)?.message}</p> : null}
      </div>
    </Drawer>
  );
}

/** A labelled text/number input row used in the trip-type form. */
function TextField({
  label,
  hint,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'number';
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-[11px] text-text-muted">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 input"
      />
      {hint ? <p className="mt-0.5 text-[11px] text-text-muted">{hint}</p> : null}
    </div>
  );
}

/** Seed the form from a trip type (edit) or empty defaults (create). */
function seedForm(t: TripType | null): FormState {
  if (!t) {
    return {
      id: '',
      name: '',
      vehicle_mode: '18',
      sort: '0',
      status: 1,
      active_icon: '',
      inactive_icon: '',
    };
  }
  return {
    id: String(t.id),
    name: t.name,
    vehicle_mode: String(t.vehicle_mode ?? 0),
    sort: String(t.sort ?? 0),
    status: t.status,
    active_icon: t.active_icon ?? '',
    inactive_icon: t.inactive_icon ?? '',
  };
}

/** Validate + convert the form into a TripTypeInput, or return an error string.
 *  Mirrors admin-svc's preValidateTripType so the staffer sees a precise message
 *  instantly; the server re-checks everything (this is a UX nicety, not the
 *  boundary). On create the id (the trip_type code) is required + positive; on
 *  edit it comes from the path so it isn't re-sent. */
function buildInput(f: FormState, isEdit: boolean): { input: TripTypeInput } | { error: string } {
  const name = f.name.trim();
  if (!name) return { error: 'Name is required.' };

  let id = 0;
  if (!isEdit) {
    const it = f.id.trim();
    const n = Number(it);
    if (it === '' || Number.isNaN(n) || n <= 0 || !Number.isInteger(n)) {
      return { error: 'Code (the trip-type id, e.g. 1 / 4 / 13 / 14) is required and must be a positive whole number.' };
    }
    id = n;
  }

  const vm = f.vehicle_mode.trim();
  let vehicle_mode = 0;
  if (vm !== '') {
    const n = Number(vm);
    if (Number.isNaN(n) || n < 0) return { error: 'Vehicle mode must be a number of zero or more.' };
    vehicle_mode = n;
  }

  const st = f.sort.trim();
  let sort = 0;
  if (st !== '') {
    const n = Number(st);
    if (Number.isNaN(n) || n < 0) return { error: 'Sort must be a number of zero or more.' };
    sort = n;
  }

  return {
    input: {
      id,
      name,
      vehicle_mode,
      sort,
      status: f.status,
      active_icon: f.active_icon.trim(),
      inactive_icon: f.inactive_icon.trim(),
    },
  };
}
